/**
 * 로깅 유틸리티
 * 개발/운영 환경별 로깅 처리
 */

/**
 * 로그 레벨
 */
export enum LogLevel {
    DEBUG = 'DEBUG',
    INFO = 'INFO',
    WARN = 'WARN',
    ERROR = 'ERROR',
}

/**
 * 로그 엔트리
 */
interface LogEntry {
    level: LogLevel;
    message: string;
    timestamp: string;
    data?: unknown;
    stackTrace?: string;
}

/**
 * Logger 클래스
 */
class Logger {
    private isProduction: boolean;
    private enableConsoleLog: boolean;

    constructor() {
        this.isProduction = process.env.NODE_ENV === 'production';
        this.enableConsoleLog = process.env.REACT_APP_ENABLE_CONSOLE_LOG === 'true';
    }

    /**
     * 로그 포맷팅
     */
    private formatLog(entry: LogEntry): string {
        const parts = [
            `[${entry.timestamp}]`,
            `[${entry.level}]`,
            entry.message,
        ];

        if (entry.data) {
            parts.push(JSON.stringify(entry.data, null, 2));
        }

        if (entry.stackTrace) {
            parts.push(entry.stackTrace);
        }

        return parts.join(' ');
    }

    /**
     * 로그 출력 여부 결정
     */
    private shouldLog(level: LogLevel): boolean {
        // 운영 환경에서는 ERROR만 출력
        if (this.isProduction) {
            return level === LogLevel.ERROR || this.enableConsoleLog;
        }
        return true;
    }

    /**
     * 로그 출력
     */
    private log(level: LogLevel, message: string, data?: unknown): void {
        if (!this.shouldLog(level)) {
            return;
        }

        const entry: LogEntry = {
            level,
            message,
            timestamp: new Date().toISOString(),
            data,
        };

        const formattedLog = this.formatLog(entry);

        switch (level) {
            case LogLevel.DEBUG:
                console.debug(formattedLog);
                break;
            case LogLevel.INFO:
                console.log(formattedLog);
                break;
            case LogLevel.WARN:
                console.warn(formattedLog);
                break;
            case LogLevel.ERROR:
                console.error(formattedLog);
                break;
        }

        // 운영 환경에서는 외부 로깅 서비스로 전송 가능
        if (this.isProduction && level === LogLevel.ERROR) {
            this.sendToRemoteLogger(entry);
        }
    }

    /**
     * 원격 로깅 서비스로 전송
     */
    private sendToRemoteLogger(entry: LogEntry): void {
        // TODO: 실제 로깅 서비스 연동 (Sentry, LogRocket 등)
        // 예시:
        // fetch('/api/logs', {
        //     method: 'POST',
        //     body: JSON.stringify(entry),
        // });
    }

    /**
     * DEBUG 레벨 로그
     */
    public debug(message: string, data?: unknown): void {
        this.log(LogLevel.DEBUG, message, data);
    }

    /**
     * INFO 레벨 로그
     */
    public info(message: string, data?: unknown): void {
        this.log(LogLevel.INFO, message, data);
    }

    /**
     * WARN 레벨 로그
     */
    public warn(message: string, data?: unknown): void {
        this.log(LogLevel.WARN, message, data);
    }

    /**
     * ERROR 레벨 로그
     */
    public error(message: string, error?: Error | unknown): void {
        const entry: LogEntry = {
            level: LogLevel.ERROR,
            message,
            timestamp: new Date().toISOString(),
            data: error,
            stackTrace: error instanceof Error ? error.stack : undefined,
        };

        const formattedLog = this.formatLog(entry);
        console.error(formattedLog);

        if (this.isProduction) {
            this.sendToRemoteLogger(entry);
        }
    }

    /**
     * API 요청 로깅
     */
    public apiRequest(method: string, url: string, data?: unknown): void {
        this.debug(`API Request: ${method} ${url}`, data);
    }

    /**
     * API 응답 로깅
     */
    public apiResponse(method: string, url: string, status: number, data?: unknown): void {
        this.debug(`API Response: ${method} ${url} [${status}]`, data);
    }

    /**
     * API 에러 로깅
     */
    public apiError(method: string, url: string, error: Error): void {
        this.error(`API Error: ${method} ${url}`, error);
    }
}

/**
 * 싱글톤 Logger 인스턴스
 */
export const logger = new Logger();
